import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {useTranslation} from 'react-i18next';

import {Modal, useModalHandle} from '@pexip/components';
import type {MediaDeviceInfoLike, IndexedDevices} from '@pexip/media-control';

import {BeforeTestingYourMic} from './BeforeTestingYourMic.view';
import {GetReadyTestingYourMic} from './GetReadyTestingYourMic.view';
import {RecordingAudio} from './RecordingAudio.view';
import {PlayingBack} from './PlayingBack.view';
import {MicrophoneMonitorTitle} from './MicrophoneMonitorTitle.view';
import {ReplayButton} from './ReplayButton.view';

import styles from './MicrophoneMonitorModal.module.scss';

export enum TestYourMicStages {
    BeforeTesting = 'before-testing',
    GetReady = 'get-ready',
    Recording = 'recording',
    RecordingSuccess = 'recording-success',
    PlayingBack = 'playing-back',
    PlaybackFinished = 'playback-finished',
}

export const MicrophoneMonitorModal: React.FC<{
    audioMeter?: React.ReactNode;
    className?: string;
    closeOnOutsideClick?: boolean;
    currentStage: TestYourMicStages;
    devices: IndexedDevices;
    getReadyCount?: number;
    isOpen: boolean;
    onClose?: () => void;
    onAudioInputChange: (id: string) => void;
    onAudioOutputChange: (id: string) => void;
    selectedAudioInput?: MediaDeviceInfoLike;
    selectedAudioOutput?: MediaDeviceInfoLike;
    startTest: () => void;
    tryAgain?: () => void;
    replay?: () => void;
}> = ({
    audioMeter,
    className,
    currentStage = TestYourMicStages.BeforeTesting,
    devices,
    getReadyCount = 3,
    selectedAudioInput,
    selectedAudioOutput,
    isOpen,
    onClose,
    onAudioInputChange,
    onAudioOutputChange,
    startTest,
    closeOnOutsideClick = false,
    tryAgain,
    replay,
}) => {
    const {t} = useTranslation();
    const recordingFinishedMessage = t(
        'media.recording-finished',
        'Recording finished',
    );
    const [screenReaderStatus, setScreenReaderStatus] = useState<{
        message: string;
        stage: TestYourMicStages;
    }>({message: '', stage: currentStage});
    const screenReaderStatusRef = useRef<HTMLSpanElement>(null);
    const currentScreenReaderStatus =
        screenReaderStatus.stage === currentStage
            ? screenReaderStatus.message
            : '';

    const {modalHandleRef, focusModal} = useModalHandle();

    const renderContent = () => {
        switch (currentStage) {
            case TestYourMicStages.GetReady:
                return <GetReadyTestingYourMic currentCount={getReadyCount} />;
            case TestYourMicStages.Recording:
            case TestYourMicStages.RecordingSuccess:
                return (
                    <RecordingAudio
                        isRecordSuccess={
                            currentStage === TestYourMicStages.RecordingSuccess
                        }
                    >
                        {audioMeter}
                    </RecordingAudio>
                );
            case TestYourMicStages.PlayingBack:
            case TestYourMicStages.PlaybackFinished:
                return (
                    <PlayingBack
                        devices={devices}
                        onAudioInputChange={onAudioInputChange}
                        onOutputInputChange={onAudioOutputChange}
                        selectedAudioInput={selectedAudioInput}
                        selectedAudioOutput={selectedAudioOutput}
                        tryAgain={tryAgain}
                    >
                        {currentStage === TestYourMicStages.PlaybackFinished ? (
                            <ReplayButton replay={replay} />
                        ) : (
                            audioMeter
                        )}
                    </PlayingBack>
                );
            default:
                return (
                    <BeforeTestingYourMic
                        onAudioInputChange={onAudioInputChange}
                        selectedAudioInput={selectedAudioInput}
                        devices={devices}
                        startTest={startTest}
                    />
                );
        }
    };

    useEffect(() => {
        if (isOpen) {
            focusModal();
        }
    }, [isOpen, focusModal]);

    useEffect(() => {
        if (currentStage !== TestYourMicStages.GetReady) {
            return;
        }

        setScreenReaderStatus(status =>
            status.stage === TestYourMicStages.GetReady && getReadyCount > 0
                ? status
                : {message: '', stage: TestYourMicStages.GetReady},
        );
        if (getReadyCount > 0) {
            const timeoutId = window.setTimeout(() => {
                setScreenReaderStatus({
                    message: String(getReadyCount),
                    stage: TestYourMicStages.GetReady,
                });
            }, 100);
            return () => window.clearTimeout(timeoutId);
        }
    }, [currentStage, getReadyCount]);

    useEffect(() => {
        if (currentStage === TestYourMicStages.PlayingBack) {
            setScreenReaderStatus(status =>
                status.stage === TestYourMicStages.PlayingBack &&
                status.message === recordingFinishedMessage
                    ? status
                    : {
                          message: recordingFinishedMessage,
                          stage: TestYourMicStages.PlayingBack,
                      },
            );
        }
    }, [currentStage, recordingFinishedMessage]);

    useLayoutEffect(() => {
        if (currentStage === TestYourMicStages.GetReady) {
            screenReaderStatusRef.current?.focus();
        }
    }, [currentStage]);

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            ref={modalHandleRef}
            padding="medium"
            isOpen={isOpen}
            closeOnOutsideClick={closeOnOutsideClick}
            onClose={onClose}
            withCloseButton
            closeButtonAriaLabel={t('common.close', 'Close')}
            scrollbarsAriaLabel={t(
                'common.scrollable-content',
                'Scrollable content',
            )}
            uniqueTitle={t(
                'media.test-your-microphone',
                'Test your microphone',
            )}
            className={cx(styles.modal, className)}
        >
            <span
                aria-atomic="true"
                aria-live="assertive"
                className={styles.screenReaderStatus}
                ref={screenReaderStatusRef}
                role="status"
                tabIndex={-1}
            >
                {currentScreenReaderStatus}
            </span>
            <MicrophoneMonitorTitle currentStage={currentStage} />
            {renderContent()}
        </Modal>
    );
};

export type MicrophoneMonitorModalProps = React.ComponentProps<
    typeof MicrophoneMonitorModal
>;
